'use client'
import React from 'react'
import { observer } from 'mobx-react-lite'

import { Button, } from '@hanzo/ui/primitives'
import { Box, MediaStack, cn } from '@hanzo/ui'
import type { LineItem } from '@hanzo/commerce/types'
import { useCommerce, formatCurrencyValue } from '@hanzo/commerce'

import CheckoutButton from '../checkout-button'
import { useCommerceDrawer } from '../../../commerce/ui/context'

const CN = {
    // h: mind padding!
  mobile: { w: 36, h: 32 },
  sm:  { w: 60, h: 34 },
  desktop: { w: 60, h: 32 },
}

const Info: React.FC<{
  item: LineItem
  clx?: string
}> = ({
  item,
  clx=''
}) => {

  const cmmc = useCommerce()
  const family = cmmc.getFamilyById(item.familyId)

  let topLine = family!.title
  if (item.sku.startsWith('LXM-AG') || item.sku.startsWith('LXM-AU')) {
    topLine = family!.parentTitle + ', ' + family!.title   
  }

  const optionLabel = item.optionLabelShort ?? item.optionLabel
  const priceStr = formatCurrencyValue(item.price)

  return (<>
    <Box className={cn('sm:hidden flex flex-col !text-muted items-stretch whitespace-nowrap text-ellipsis ', clx)}>
      <Box tag="p" className='text-left text-xs'>{topLine}</Box>
      <Box className='flex justify-between text-xxs'>
        <div>{optionLabel}</div>
        <Box className='pl-2.5'>{priceStr}</Box>
      </Box>
    </Box>
    <Box className={cn('hidden sm:flex !text-muted  flex-col items-stretch whitespace-nowrap text-ellipsis text-xs', clx)}>
      <Box tag="p" className='text-left'>{topLine}</Box>
      <Box className='flex justify-between'>
        <div>{optionLabel}</div>
        <Box className='pl-2.5'>{priceStr}</Box>
      </Box>
    </Box>
  </>)
}

const PROMPT_COMMON_CLX = 'block absolute top-0 left-0 bg-transparent duration-400 transition-opacity' 

const Micro: React.FC<{
  clx?: string
  handleCheckout: () => void
  handleItemClicked: () => void 
}> = observer(({
  clx='',
  handleCheckout,
  handleItemClicked 
}) => {

  const drawer = useCommerceDrawer()
  const cmmc = useCommerce()

  return (        
    <Box className={cn(
      (drawer.showRecent ? 'grid grid-cols-2' : 'flex justify-center items-center '),
      (drawer.showRecent ? ((drawer.isMobile) ? '-mt-3' : '-mt-3') : ''),
      'gap-2 md:gap-3 relative',
      clx
    )}>
    {drawer.showRecent && (
      <Box className={'flex flex-col items-stretch ' + (drawer.isMobile ? 'justify-start' : 'group')}>
        <Box tag="p" className={'relative text-muted text-xxs md:text-xs leading-none pl-1 self-start ' + (drawer.isMobile ? 'top-[3px]' : 'top-[1px]')}>
          <Box tag="span" className='invisible'>scrictly for layout</Box>
          {drawer.isMobile ? (
            <Box tag="span" className={PROMPT_COMMON_CLX}>tap for options:</Box>
          ) : (<>
            <Box tag="span" className={PROMPT_COMMON_CLX + ' group-hover:opacity-0'}>recent item:</Box>
            <Box tag="span" className={PROMPT_COMMON_CLX + ' opacity-0 group-hover:opacity-100'}>view options:</Box>
          </>)}
        </Box>
        <Button 
          variant='ghost'
          className={cn(`rounded-${drawer.isMobile ? 'md' : 'lg'}`, cn(
            'box-content',
            'flex flex-row justify-between items-center gap-1',
            '-ml-1.5',
            'overflow-hidden ', 
            'px-1 md:px-2 py-[2px]',
            'border border-transparent group-hover:border-muted-3',
            'group-hover:!bg-transparent',
            '!min-w-0'
          ))}
          size={drawer.isMobile ? 'sm' : 'default'}
          onClick={handleItemClicked}
          
        >
          {cmmc.recentItem?.item.img && (<>
            <MediaStack media={{img: cmmc.recentItem.item.img}} constrainTo={CN.mobile} className='sm:hidden grow-0 shrink-0' />
            <MediaStack media={{img: cmmc.recentItem.item.img}} constrainTo={CN.sm} className='hidden sm:block md:hidden grow-0 shrink-0' />
            <MediaStack media={{img: cmmc.recentItem.item.img}} constrainTo={CN.desktop} className='hidden md:block grow-0 shrink-0' />
          </>)} 
          {cmmc.recentItem && (
            <Box className='grow w-full'>
              <Info item={cmmc.recentItem.item} clx='w-full'/>
            </Box>
          )}
        </Button>
      </Box>
    )}
    {drawer.showCheckout && (
      <Box className={cn(
        'flex flex-col w-full', 
        (drawer.showRecent ? 'items-stretch' : 'items-center' ),
        (drawer.isMobile ? 'justify-start' : 'justify-center')
      )}>
        {drawer.showRecent && <Box tag="p" className='invisible text-muted text-xxs md:text-xs leading-none pl-1 self-start'>for layout</Box>}
        <CheckoutButton 
          handleCheckout={handleCheckout} 
          variant='primary' 
          size={drawer.isMobile ? 'sm' : 'lg'}
          className={cn(`rounded-${drawer.isMobile ? 'md' : 'lg'}`, cn(drawer.isMobile ? 
            (drawer.showRecent ? 'pl-3.5 pr-2.5' : 'min-w-[320px]')
            :
            (drawer.showRecent ? '' : 'w-[320px]'),
            'text-sm font-semibold'
          ))}
          centerText={drawer.isMobile ? !drawer.showRecent : true}
           
        />
      </Box>
    )}
    </Box>
  )
})

export default Micro
